import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { motion } from 'motion/react';
import { cardPos, cn, isPhrasalFront } from '@shared/lib';
import { toast } from '@shared/ui';
import type { LookupResult } from '../api/workspaceApi';
import { useChoicePrice } from '../lib/useChoicePrice';
import { ChoiceTokenLine, createButtonLabel } from './ChoiceSummary';
import {
  groupByPos,
  isLocked,
  isRare,
  pagePhrases,
  PHRASE_PAGER,
  sectionsToOpen,
  type PhrasePager,
  useSenseChoice,
  type ChoiceCard,
  type ConstructionItem,
  type SenseChoice,
  type SenseItem,
} from '../lib/senseChoice';
import { markLabels } from '../lib/usageMarks';
import { hasDetails, posName } from './SensePicker';

/** Полное название части речи («существительное»); фолбэк — сырой pos словаря. */
export function posFull(t: TFunction, pos: string | null): string {
  return posName(t, pos);
}

/** Сокращение части речи («сущ.»); фолбэк — полное название. */
export function posShortLabel(t: TFunction, pos: string | null): string {
  return posName(t, pos, true);
}

// v2 (2026-10-06): подсказка закрывается только крестиком; прежний ключ ставился уже при
// первом долгом нажатии, и плашку многие больше не видели.
const COACH_KEY = 'lx.pickCoach.v2';
const HOLD_MS = 420;

function BackIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="M19 12H5M11 6l-6 6 6 6" />
    </svg>
  );
}
function CheckIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="M5 12.5l4.5 4.5L19 7.5" />
    </svg>
  );
}
function ChevDown({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="M6 9l6 6 6-6" />
    </svg>
  );
}
function XIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className={className} aria-hidden>
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  );
}

function readCoach(): boolean {
  try {
    return window.localStorage.getItem(COACH_KEY) !== '0';
  } catch {
    return true;
  }
}
function hideCoachForever() {
  try {
    window.localStorage.setItem(COACH_KEY, '0');
  } catch {
    // хранилище недоступно — подсказка просто покажется снова
  }
}

/** «ходить (за покупками)» → основной текст и уточнение в скобках мельче. */
function splitParen(text: string): [string, string] {
  const m = text.match(/^(.+?)\s(\(.+\))$/);
  return m ? [m[1], m[2]] : [text, ''];
}

/** Подробности значения во всплывашке долгого нажатия. */
function Details({ x }: { x: SenseItem }) {
  const { t } = useTranslation();
  const marks = markLabels(t, x, true);
  const synonyms = x.translations.slice(1);
  return (
    <div className="flex flex-col gap-2 text-left text-fg">
      <div className="flex items-start gap-2">
        <div className="flex min-w-0 flex-1 flex-wrap items-baseline gap-x-2 gap-y-0.5 pt-0.5">
          <span className="text-[15px] font-semibold">{x.main}</span>
          <span className="font-serif text-[14px] italic text-muted">{posName(t, x.pos)}</span>
        </div>
      </div>
      {marks.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {marks.map((m) => (
            <span key={m} className="inline-flex h-[22px] items-center rounded-[6px] bg-[#3a3a40] px-2 text-[12px] text-[#d6d1ca]">
              {m}
            </span>
          ))}
        </div>
      )}
      {synonyms.length > 0 && (
        <div className="text-[14px] leading-[1.45] text-[#c9c5c0]">
          <span className="text-[#8a8a90]">{t('cardsWorkspace.pick.synonyms')}</span>
          {synonyms.join(', ')}
        </div>
      )}
      {x.usageNote && <div className="text-[14px] leading-[1.45] text-[#e4dfd8] [text-wrap:pretty]">{x.usageNote}</div>}
      {x.example && (
        <div className="rounded-[10px] bg-[#1e1e20] px-3 py-2.5">
          <div className="font-serif text-[15px] italic leading-[1.35]">{x.example.en}</div>
          <div className="mt-[3px] text-[14px] leading-[1.4] text-muted">{x.example.ru}</div>
        </div>
      )}
    </div>
  );
}

function tileTone(x: SenseItem, on: boolean) {
  if (on) return { bg: 'rgba(243,106,28,0.14)', line: '#f36a1c', fg: '#f5f1ec', dotBg: '#f36a1c', ring: '#f36a1c', dotFg: '#141416', meta: '#ff9a5c' };
  if (x.learned) return { bg: 'rgba(61,214,140,0.07)', line: 'rgba(61,214,140,0.3)', fg: '#3dd68c', dotBg: 'rgba(61,214,140,0.18)', ring: '#3dd68c', dotFg: '#3dd68c', meta: '#3dd68c' };
  if (x.generating) return { bg: 'rgba(243,106,28,0.06)', line: 'rgba(243,106,28,0.3)', fg: '#ff9a5c', dotBg: 'transparent', ring: '#ff9a5c', dotFg: '#ff9a5c', meta: '#ff9a5c' };
  return { bg: '#1e1e20', line: '#2e2e34', fg: '#f5f1ec', dotBg: 'transparent', ring: '#4a4a50', dotFg: '#141416', meta: '#8a8a90' };
}

// Редкое значение только для чтения: без кружка и заливки, подробности — по долгому нажатию.
const READ_ONLY_TONE = {
  bg: 'transparent',
  line: '#2a2a30',
  fg: '#a9a9ae',
  dotBg: 'transparent',
  ring: 'transparent',
  dotFg: 'transparent',
  meta: '#6a6a70',
};

interface TileCtx {
  choice: SenseChoice;
  yandex: boolean;
  onTap: (x: SenseItem) => void;
  onHoldStart: (x: SenseItem) => void;
  onHoldEnd: () => void;
  /** Редкие значения: не отмечаются (2026-10-07). */
  readOnly?: boolean;
}

/** Плитка значения с кружком (макет «Создание карточек мобайл v2»): тап — выбрать,
 *  долгое нажатие — подсмотреть пример и пометы, не отмечая. */
function Tile({ x, wide, ctx }: { x: SenseItem; wide: boolean; ctx: TileCtx }) {
  const { t } = useTranslation();
  const { choice } = ctx;
  const locked = isLocked(x);
  const ro = !!ctx.readOnly && !locked;
  const on = !ro && !locked && choice.isSelected(x.id);
  const dim = !ro && !on && !locked && choice.atLimit;
  const tone = ro ? READ_ONLY_TONE : tileTone(x, on);
  const marks = ctx.yandex ? [] : markLabels(t, x);
  const tag = x.learned
    ? t('cardsWorkspace.pick.tagLearned')
    : x.generating
      ? t('cardsWorkspace.pick.tagGenerating')
      : '';
  const meta = [...marks, tag].filter(Boolean).join(' · ');
  const [text, sub] = splitParen(x.main);
  const info = hasDetails(x, ctx.yandex);
  return (
    // До 360 px сетка в одну колонку — span 2 там добавил бы неявную вторую колонку.
    <div className={cn('relative flex min-w-0', wide && 'min-[360px]:col-span-2')}>
      <button
        type="button"
        onClick={ro ? undefined : () => ctx.onTap(x)}
        onPointerDown={info ? () => ctx.onHoldStart(x) : undefined}
        onPointerUp={info ? ctx.onHoldEnd : undefined}
        onPointerLeave={info ? ctx.onHoldEnd : undefined}
        onPointerCancel={info ? ctx.onHoldEnd : undefined}
        onContextMenu={(e) => e.preventDefault()}
        aria-pressed={ro ? undefined : on}
        aria-disabled={ro || locked || dim || undefined}
        style={{ background: tone.bg, boxShadow: `inset 0 0 0 1px ${tone.line}`, color: tone.fg }}
        className={cn(
          'flex min-h-12 min-w-0 flex-1 select-none items-center gap-2.5 rounded-[14px] px-3 py-1.5 text-left transition-[background,box-shadow,opacity] duration-150 [-webkit-tap-highlight-color:transparent] [-webkit-touch-callout:none]',
          dim && 'opacity-[0.38]',
        )}
      >
        {!ro && (
          <span
            className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full transition-colors"
            style={{ background: tone.dotBg, boxShadow: `inset 0 0 0 1.5px ${tone.ring}`, color: tone.dotFg }}
          >
            {(on || x.learned) && <CheckIcon className="h-3 w-3" />}
            {x.generating && <span className="h-[7px] w-[7px] animate-pulse rounded-full bg-current" />}
          </span>
        )}
        <span className="flex min-w-0 flex-1 flex-col gap-px">
          {/* Перевод рвётся только по слогам с дефисом (lang="ru" — словарь переносов и в английском интерфейсе),
              а не посреди слова: «ответствен|ность» на 320 px (приёмка A1-05). break-words — запасной выход, если
              словаря переносов у браузера нет: лучше разрыв, чем слово за краем плитки. */}
          <span lang="ru" className="break-words text-[15px] font-medium leading-[1.25] hyphens-auto">
            {text}
            {/* Пробел, а не отступ: «покрывать (сверху)» иначе не переносилось и вылезало за плитку. */}
            {sub && (
              <>
                {' '}
                <span className="text-[14px] font-normal text-[#8a8a90]">{sub}</span>
              </>
            )}
          </span>
          {meta && (
            <span className="text-[12px] font-medium leading-[1.3]" style={{ color: tone.meta }}>
              {meta}
            </span>
          )}
        </span>
      </button>
    </div>
  );
}

/** Длинные значения — на всю ширину; нечётную одиночку тоже растягиваем (макет). */
function tileSpans(items: SenseItem[]): boolean[] {
  const wide = items.map((x) => {
    const [, sub] = splitParen(x.main);
    return x.main.length - sub.length * 0.25 > 17;
  });
  const singles = wide.map((w, i) => (w ? -1 : i)).filter((i) => i >= 0);
  if (singles.length % 2) wide[singles[singles.length - 1]] = true;
  return wide;
}

function TileGrid({ items, ctx }: { items: SenseItem[]; ctx: TileCtx }) {
  const spans = tileSpans(items);
  return (
    // 320–359 px — одна колонка во всю ширину: в двух перевод в ~100 px рвался посреди слова (приёмка A1-05).
    <div className="grid grid-cols-1 gap-1.5 [grid-auto-flow:row_dense] min-[360px]:grid-cols-2">
      {items.map((x, i) => (
        <Tile key={x.id} x={x} wide={spans[i]} ctx={ctx} />
      ))}
    </div>
  );
}

function SectionHead({
  title,
  count,
  selected,
  open,
  preview,
  onToggle,
}: {
  title: string;
  count: number;
  selected: number;
  open: boolean;
  preview?: string;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      className="flex min-h-14 w-full items-center gap-2.5 px-4 py-2 text-left text-fg"
    >
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="flex min-w-0 items-center gap-2">
          {/* 320–359 px — 17 px у всех разделов: «Активно используются» (199 px) встаёт рядом со счётчиком и «✓ N»,
              в 18 px резалось многоточием (R2-08). Многоточие — запасной выход для «12» и «✓ 10». */}
          <span className="min-w-0 truncate font-serif text-[18px] font-medium max-[359.98px]:text-[17px]">{title}</span>
          <span className="shrink-0 text-[14px] text-[#8a8a90]">{count}</span>
          {selected > 0 && (
            <span className="inline-flex h-5 shrink-0 items-center whitespace-nowrap rounded-pill bg-accent px-[7px] text-[12px] font-bold text-[#141416]">
              ✓ {selected}
            </span>
          )}
        </span>
        {!open && preview && <span className="truncate text-[14px] text-[#6a6a70]">{preview}</span>}
      </span>
      <ChevDown className={cn('h-[18px] w-[18px] shrink-0 text-muted transition-transform duration-200', open && 'rotate-180')} />
    </button>
  );
}

function PillButton({ children, onClick, accent, dashed }: { children: React.ReactNode; onClick: () => void; accent?: boolean; dashed?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex h-11 items-center gap-1.5 rounded-pill px-3.5 text-[14px]',
        accent && 'bg-accent/[0.12] px-4 font-semibold text-[#ff9a5c]',
        dashed && 'border border-dashed border-[#3a3a40] px-4 font-medium text-[#c9c5c0]',
        !accent && !dashed && 'font-medium text-muted',
      )}
    >
      {children}
    </button>
  );
}


/** Редкая фраза или фразовый глагол — только для чтения (2026-10-07): фраза и переводы
 *  значений без кружка; значение, которое уже учат, помечено. */
function RarePhraseRow({ c, t }: { c: ConstructionItem; t: TFunction }) {
  const learned = c.senses.some((x) => x.learned);
  const marks = markLabels(t, { registers: c.registers, region: c.region, grammar: [] });
  const shown = c.senses.slice(0, 3).map((x) => x.main);
  return (
    <div className="flex min-w-0 flex-col gap-0.5 px-1 py-2">
      <span className="font-serif text-[16px] font-medium leading-[1.25]" style={{ color: learned ? '#3dd68c' : '#c9c5c0' }}>
        {c.phrase}
      </span>
      <span className="text-[14px] leading-[1.35] text-[#8a8a90] [overflow-wrap:anywhere]">
        {shown.join(', ')}
        {c.senses.length > shown.length && ` +${c.senses.length - shown.length}`}
        {marks.length > 0 && <span className="ml-1.5 text-[12px] text-[#6a6a70]">{marks.join(' · ')}</span>}
        {learned && (
          <span className="ml-1.5 text-[12px] font-semibold uppercase tracking-[0.08em] text-[#3dd68c]">
            {t('cardsWorkspace.pick.tagLearned')}
          </span>
        )}
      </span>
    </div>
  );
}

interface ChooseTranslationsProps {
  word: string;
  data: LookupResult;
  submitting: boolean;
  onBack: () => void;
  /** Создать: выбранные значения слова и фразы — каждая фраза своей карточкой. */
  onCreate: (cards: ChoiceCard[]) => void;
  /** Общий выбор (`useSenseChoice` у родителя): «←» и повторный вход сохраняют отметки.
   *  Без него экран держит свой выбор — он пропадает вместе с экраном. */
  choice?: SenseChoice;
  /** Строка модуля над составом, в зоне большого пальца (главная, ТЗ § 7 п. 4). */
  moduleSlot?: ReactNode;
  /** Ошибка создания строкой над кнопкой (главная); на `/cards/new` ошибки — тостами. */
  error?: string | null;
  /** Строка над кнопкой с тоном (главная): «Связь прервалась — проверяем…», «Ничего не добавлено…» —
   *  приглушённые, ошибки — красные. `error` важнее. */
  notice?: { tone: 'danger' | 'muted'; text: string } | null;
  /** Текстовая кнопка справа от строки `notice` (главная: «Повторить» у ошибки перечитывания ответа
   *  — 429 и 5xx, ТЗ главной § 23.3). Без неё строка — только текст, как на `/cards/new`. */
  noticeAction?: { label: string; onClick: () => void };
  /** «Проверяем…»: ответ перечитывается (сменили модуль) или проверяется исход нажатия — кнопка неактивна. */
  pending?: boolean;
  /** Кнопка неактивна со своей обычной подписью (главная: перечитать ответ не удалось, ТЗ § 23.3). */
  createDisabled?: boolean;
  /** Под составом, когда жетоны закончились (главная: «Добавить вручную», подарок, «Тарифы»). */
  exhaustedActions?: ReactNode;
}

/**
 * Мобильный шаг 2 создания карточек (макет «Создание карточек мобайл v2», 2026-10-06):
 * полный экран без клавиатуры, четыре раздела словаря аккордеоном. Значения — плитки с
 * кружком: тап только выбирает, пример и пометы — по долгому нажатию (с 2026-10-07 тап
 * больше не раскрывает подробности под группой — решение владельца). Слово и каждая
 * фраза — отдельные карточки.
 */
export function ChooseTranslations({
  word,
  data,
  submitting,
  onBack,
  onCreate,
  choice: sharedChoice,
  moduleSlot,
  error,
  notice,
  noticeAction,
  pending = false,
  createDisabled = false,
  exhaustedActions,
}: ChooseTranslationsProps) {
  const { t } = useTranslation();
  // Свой выбор — только когда родитель не передал общий (хуки вызываются всегда).
  const ownChoice = useSenseChoice(sharedChoice ? null : data);
  const choice = sharedChoice ?? ownChoice;
  const view = choice.view!;
  const yandex = view.source === 'yandex';

  // «Ввести снова» отметил значение в свёрнутом по умолчанию разделе («Другие», «Фразы», «Фразовые глаголы») —
  // раздел открыт при показе этапа 2: платное нажатие показывает, за что платят (ТЗ главной § 23.6). Решается при
  // показе и при новых отметках, дальше раздел в руках ученика; без отметок разделы как всегда.
  const openAtStart = (): Record<string, boolean> => {
    const marked = sectionsToOpen(view, choice.initialIds, choice.isSelected);
    return {
      ...(marked.other ? { o: true } : {}),
      ...(marked.phrases ? { ph: true } : {}),
      ...(marked.phrasalVerbs ? { pv: true } : {}),
    };
  };
  const [open, setOpen] = useState<Record<string, boolean>>(openAtStart);
  const [openDecidedFor, setOpenDecidedFor] = useState(choice.initialIds);
  if (openDecidedFor !== choice.initialIds) {
    setOpenDecidedFor(choice.initialIds);
    setOpen((prev) => ({ ...prev, ...openAtStart() }));
  }
  const [pagers, setPagers] = useState<Record<string, PhrasePager>>({});
  const [row, setRow] = useState<string | null>(null);
  const [coach, setCoach] = useState(readCoach);
  const [peek, setPeek] = useState<string | null>(null);
  const holdTimer = useRef<number | undefined>(undefined);
  const holdFired = useRef(false);
  const offPeek = useRef<(() => void) | null>(null);
  useEffect(
    () => () => {
      window.clearTimeout(holdTimer.current);
      offPeek.current?.();
    },
    [],
  );

  const otherCommon = view.other.filter((x) => !isRare(x));
  const otherRare = view.other.filter(isRare);
  const isOpen = (k: string) => {
    if (open[k] !== undefined) return open[k];
    if (k === 'a') return true;
    if (k === 'o' || k === 'ph') return !view.active.length;
    // «Редкие» свёрнуты, если есть что-то ещё (решение владельца 2026-10-06).
    if (k === 'r') return !view.active.length && !otherCommon.length && !constructions.length;
    return false;
  };
  const toggleSec = (k: string) => setOpen((o) => ({ ...o, [k]: !isOpen(k) }));

  const onTap = (x: SenseItem) => {
    if (holdFired.current) {
      holdFired.current = false;
      return;
    }
    if (x.learned) {
      toast({ tone: 'info', message: t('cardsWorkspace.pick.titleLearned') });
      return;
    }
    if (x.generating) {
      toast({ tone: 'info', message: t('cardsWorkspace.pick.titleGenerating') });
      return;
    }
    const res = choice.toggle(x.id);
    if (res === 'limit') toast({ tone: 'info', message: t('cardsWorkspace.pick.titleLimit', { limit: choice.limit }) });
  };

  const onHoldStart = (x: SenseItem) => {
    holdFired.current = false;
    window.clearTimeout(holdTimer.current);
    holdTimer.current = window.setTimeout(() => {
      holdFired.current = true;
      setPeek(x.id);
      if (navigator.vibrate) navigator.vibrate(8);
      // Закрывается, только когда палец отпущен (2026-10-07). pointercancel/touchcancel не
      // слушаем: браузер шлёт их, когда сам забирает жест (прокрутка, системное меню), — и
      // подробности пропадали. Пока они открыты, прокрутку и системное меню долгого нажатия
      // гасим: меню на Android приходит через ~0,5 с уже на слой подробностей, и без отмены
      // жест обрывался сразу после открытия. Если отпускание всё же не дошло — крестик и фон.
      const events = ['pointerup', 'touchend'];
      const noScroll = (e: TouchEvent) => e.preventDefault();
      const noMenu = (e: Event) => e.preventDefault();
      const off = () => {
        events.forEach((n) => window.removeEventListener(n, off, true));
        window.removeEventListener('touchmove', noScroll, true);
        window.removeEventListener('contextmenu', noMenu, true);
        offPeek.current = null;
        setPeek(null);
      };
      offPeek.current?.();
      events.forEach((n) => window.addEventListener(n, off, true));
      window.addEventListener('touchmove', noScroll, { capture: true, passive: false });
      window.addEventListener('contextmenu', noMenu, true);
      offPeek.current = off;
    }, HOLD_MS);
  };
  // Палец ушёл с плитки до срабатывания — это прокрутка, а не долгое нажатие.
  const onHoldEnd = () => window.clearTimeout(holdTimer.current);
  const closePeek = () => (offPeek.current ? offPeek.current() : setPeek(null));

  const ctx: TileCtx = { choice, yandex, onTap, onHoldStart, onHoldEnd };
  const peekX = peek ? view.all.find((x) => x.id === peek) : undefined;
  const selectedIn = (list: SenseItem[]) => list.filter((x) => choice.isSelected(x.id)).length;

  const senseGroups = (list: SenseItem[], groupCtx: TileCtx = ctx) =>
    groupByPos(list).map((g, i) => (
      <div key={`${g.pos ?? 'x'}-${i}`} className="flex flex-col">
        <div className={cn('pb-3 pt-2.5', i > 0 && 'border-t border-[#26262b]')}>
          <div className="flex items-baseline gap-2 px-0.5 pb-2">
            <span className="text-[12px] font-semibold uppercase tracking-[0.12em] text-muted">{posName(t, cardPos(g.pos, isPhrasalFront(choice.view?.word)))}</span>
            <span className="text-[12px] text-[#6a6a70]">{g.items.length}</span>
          </div>
          <TileGrid items={g.items} ctx={groupCtx} />
        </div>
      </div>
    ));

  const constructionRow = (c: ConstructionItem) => {
    const single = c.senses.length === 1;
    const x0 = c.senses[0];
    const selN = c.senses.filter((x) => choice.isSelected(x.id)).length;
    const openRow = !single && row === c.id;
    const locked = single && isLocked(x0);
    const on = single && selN === 1;
    const dim = single && !on && !locked && choice.atLimit;
    const marks = markLabels(t, {
      registers: single ? [...c.registers, ...x0.registers] : c.registers,
      region: single ? [...c.region, ...x0.region] : c.region,
      grammar: single ? x0.grammar : [],
    });
    return (
      <div
        key={c.id}
        className={cn('rounded-[14px] transition-[background,box-shadow] duration-150', dim && 'opacity-40')}
        style={{
          background: selN ? 'rgba(243,106,28,0.07)' : openRow ? '#1c1c1f' : 'transparent',
          boxShadow: `inset 0 0 0 1px ${selN ? 'rgba(243,106,28,0.35)' : openRow ? '#2a2a30' : 'transparent'}`,
        }}
      >
        <div className="flex items-center">
          <button
            type="button"
            onClick={single ? () => onTap(x0) : () => setRow(openRow ? null : c.id)}
            aria-pressed={single ? on : undefined}
            aria-expanded={single ? undefined : openRow}
            className="grid min-h-14 min-w-0 flex-1 grid-cols-[20px_minmax(0,1fr)_auto] items-center gap-3 rounded-[14px] py-2 pl-3 pr-1 text-left text-fg [-webkit-tap-highlight-color:transparent]"
          >
            <span className="flex h-5 w-5 items-center justify-center">
              {single ? (
                <span
                  className="flex h-5 w-5 items-center justify-center rounded-full"
                  style={{
                    background: on ? '#f36a1c' : x0.learned ? 'rgba(61,214,140,0.15)' : 'transparent',
                    boxShadow: `inset 0 0 0 1.5px ${on ? '#f36a1c' : x0.learned ? '#3dd68c' : x0.generating ? '#ff9a5c' : '#4a4a50'}`,
                    color: on ? '#141416' : '#3dd68c',
                  }}
                >
                  {(on || x0.learned) && <CheckIcon className="h-3 w-3" />}
                </span>
              ) : (
                <svg viewBox="0 0 24 24" fill="none" stroke="#9a9a9a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={cn('h-4 w-4 transition-transform duration-200', openRow && 'rotate-90')} aria-hidden>
                  <path d="M9 6l6 6-6 6" />
                </svg>
              )}
            </span>
            <span className="flex min-w-0 flex-col gap-0.5">
              <span className="font-serif text-[17px] font-medium leading-[1.25]" style={{ color: single && x0.learned ? '#3dd68c' : '#f5f1ec' }}>
                {c.phrase}
              </span>
              <span className="text-[14px] leading-[1.35] text-[#c9c5c0]">
                {/* Одно значение — два перевода («при этом, попутно»), как на десктопе. */}
                {single ? x0.translations.slice(0, 2).join(', ') : x0.main}
                {marks.length > 0 && <span className="ml-1.5 text-[12px] font-medium text-[#8a8a90]">{marks.join(' · ')}</span>}
                {locked && (
                  <span
                    className="ml-1.5 text-[12px] font-semibold uppercase tracking-[0.08em]"
                    style={{ color: x0.learned ? '#3dd68c' : '#ff9a5c' }}
                  >
                    {x0.learned ? t('cardsWorkspace.pick.tagLearned') : t('cardsWorkspace.pick.tagGenerating')}
                  </span>
                )}
              </span>
            </span>
            <span className="flex items-center gap-1.5 whitespace-nowrap">
              {!single && selN > 0 && (
                <span className="inline-flex h-[22px] items-center rounded-pill bg-accent px-2 text-[12px] font-bold text-[#141416]">✓ {selN}</span>
              )}
              {!single && <span className="text-[14px] text-[#8a8a90]">+{c.senses.length - 1}</span>}
            </span>
          </button>
        </div>
        {openRow && (
          <div className="px-2 pb-2">
            <TileGrid items={c.senses} ctx={ctx} />
          </div>
        )}
      </div>
    );
  };

  const constructionSection = (k: string, title: string, list: ConstructionItem[]) => {
    const sectionOpen = isOpen(k);
    const keep = (c: ConstructionItem) => row === c.id || c.senses.some((x) => choice.isSelected(x.id));
    const page = pagePhrases(list, pagers[k] ?? PHRASE_PAGER, keep);
    const setPager = (next: (p: PhrasePager) => PhrasePager) =>
      setPagers((all) => ({ ...all, [k]: next(all[k] ?? PHRASE_PAGER) }));
    const selected = list.reduce((n, c) => n + selectedIn(c.senses), 0);
    return (
      <div key={k} className="border-t border-[#26262b]">
        <SectionHead
          title={title}
          count={list.length}
          selected={selected}
          open={sectionOpen}
          onToggle={() => toggleSec(k)}
          preview={list.slice(0, 4).map((c) => c.phrase).join(', ') + (list.length > 4 ? '…' : '')}
        />
        {sectionOpen && (
          <div className="flex flex-col gap-1 px-2 pb-4">
            {page.visible.map(constructionRow)}
            {(page.more > 0 || page.canCollapse) && (
              <div className="flex flex-wrap gap-1.5 px-1 pt-1">
                {page.more > 0 && (
                  <PillButton dashed onClick={() => setPager(page.expand)}>
                    {t('cardsWorkspace.pick.more', { n: page.more })}
                    <ChevDown className="h-3 w-3" />
                  </PillButton>
                )}
                {page.canCollapse && (
                  <PillButton onClick={() => setPager(() => PHRASE_PAGER)}>{t('cardsWorkspace.pick.less')}</PillButton>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const constructions = [
    ['ph', t('cardsWorkspace.pick.phrases'), view.phrases],
    ['pv', t('cardsWorkspace.pick.phrasalVerbs'), view.phrasalVerbs],
  ].filter(([, , list]) => (list as ConstructionItem[]).length > 0) as [string, string, ConstructionItem[]][];

  const n = choice.cards.length;
  // Цена — на кнопке, справа — остаток жетонов; жетоны кончились — кнопка неактивна (ТЗ § 7 п. 2–3).
  const price = useChoicePrice(n, choice.needsGeneration);
  const canCreate = n > 0 && !submitting && !pending && !createDisabled && !price.blocked;
  const createLabel = pending
    ? t('cardsWorkspace.choose.checking')
    : n > 0
      ? createButtonLabel(t, n, price)
      : choice.allLocked
        ? t('cardsWorkspace.choose.allAdded')
        : t('cardsWorkspace.choose.pickSomething');

  // Пока палец держит значение — его подробности по центру экрана, фон приглушён
  // (решение владельца 2026-10-06). Отпустил — закрылось (слушатели в onHoldStart).
  const peekLayer =
    peekX &&
    createPortal(
      // Подложка и крестик — запасной выход, если отпускание пальца не дошло (2026-10-07).
      <div
        className="fixed inset-0 z-[90] flex select-none items-center justify-center bg-black/60 px-6 [-webkit-touch-callout:none]"
        onClick={closePeek}
        onContextMenu={(e) => e.preventDefault()}
      >
        <div
          className="relative w-full max-w-[340px] rounded-2xl bg-[#2a2a2f] px-4 pb-4 pt-3.5 shadow-[0_0_0_1px_#3a3a40,0_24px_48px_-12px_rgba(0,0,0,0.85)]"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            onClick={closePeek}
            aria-label={t('cardsWorkspace.pick.peekClose')}
            className="absolute right-1.5 top-1.5 grid h-9 w-9 place-items-center rounded-full text-muted active:bg-[#34343a]"
          >
            <XIcon className="h-4 w-4" />
          </button>
          <div className="pr-8">
            <Details x={peekX} />
          </div>
        </div>
      </div>,
      document.body,
    );

  return (
    <motion.div
      initial={{ x: '100%' }}
      animate={{ x: 0 }}
      exit={{ x: '100%' }}
      transition={{ duration: 0.24, ease: [0.2, 0.9, 0.2, 1] }}
      // Без выделения текста на всём экране (2026-10-07): долгое нажатие на Android выделяло
      // слово, открывалась панель поиска Google и забирала жест — подробности закрывались.
      className="fixed inset-0 z-[80] flex select-none flex-col bg-base [-webkit-touch-callout:none]"
      style={{ paddingTop: 'env(safe-area-inset-top)' }}
      role="dialog"
      aria-label={t('cardsWorkspace.choose.inviteTitle')}
    >
      {/* На 768–900 px шапка, содержимое и низ — не шире 640 px, по центру (ТЗ § 7 п. 7). */}
      <div className="mx-auto flex min-h-[60px] w-full max-w-[640px] shrink-0 items-center gap-1 pl-1 pr-3.5">
        <button
          type="button"
          onClick={onBack}
          aria-label={t('cardsWorkspace.choose.back')}
          className="grid h-11 w-11 shrink-0 place-items-center rounded-full text-fg active:bg-elev"
        >
          <BackIcon className="h-[22px] w-[22px]" />
        </button>
        <div className="flex min-w-0 flex-1 flex-wrap items-baseline gap-x-2">
          <span lang="en" className="font-serif text-[27px] font-medium leading-[1.15] [overflow-wrap:anywhere]">
            {word}
          </span>
          {data.transcription && <span className="font-serif text-[15px] text-muted">{data.transcription}</span>}
        </div>
        {yandex && (
          <span className="shrink-0 text-right text-[12px] leading-[1.3] text-[#6a6a70]">{t('cardsWorkspace.pick.yandexShort')}</span>
        )}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto [scrollbar-width:none]">
        <div className="mx-auto w-full max-w-[640px]">
          {coach && !yandex && (
            <div className="mx-4 mb-3 flex items-center gap-3 rounded-[14px] bg-accent/[0.08] py-1.5 pl-3 pr-1 shadow-[inset_0_0_0_1px_rgba(243,106,28,0.25)]">
              <span className="relative flex h-7 w-7 shrink-0 items-center justify-center">
                <span className="absolute inset-0 animate-pulse rounded-full shadow-[inset_0_0_0_1.5px_rgba(255,154,92,0.55)]" />
                <span className="h-3 w-3 rounded-full bg-[#ff9a5c]" />
              </span>
              <span className="min-w-0 flex-1 text-[14px] leading-[1.4] text-[#e4dfd8]">
                <b className="font-semibold text-fg">{t('cardsWorkspace.pick.coachBold')}</b>
                {t('cardsWorkspace.pick.coachRest')}
              </span>
              <button
                type="button"
                onClick={() => {
                  setCoach(false);
                  hideCoachForever();
                }}
                aria-label={t('cardsWorkspace.pick.coachHide')}
                className="grid h-10 w-10 shrink-0 place-items-center rounded-full text-muted"
              >
                <XIcon className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
          {!view.active.length && constructions.length > 0 && (
            <div className="mx-4 mb-3 flex items-start gap-2.5 rounded-[14px] bg-[#24242a] px-3 py-2.5 text-[14px] leading-[1.4] text-[#e4dfd8]">
              <span className="grid h-[22px] w-[22px] shrink-0 place-items-center rounded-[7px] bg-[#34343a] font-serif text-[14px] font-semibold italic text-[#c9c5c0]">
                i
              </span>
              {view.other.length ? t('cardsWorkspace.pick.noActiveRare') : t('cardsWorkspace.pick.noActiveNone')}
            </div>
          )}

          {view.active.length > 0 && (
            <div className="border-t border-[#26262b]">
              <SectionHead
                title={t('cardsWorkspace.pick.active')}
                count={view.active.length}
                selected={selectedIn(view.active)}
                open={isOpen('a')}
                onToggle={() => toggleSec('a')}
                preview={view.active.slice(0, 6).map((x) => x.main).join(', ') + (view.active.length > 6 ? '…' : '')}
              />
              {isOpen('a') && (
                <div className="flex flex-col px-4 pb-4">
                  {senseGroups(view.active)}
                </div>
              )}
            </div>
          )}

          {otherCommon.length > 0 && (
            <div className="border-t border-[#26262b]">
              <SectionHead
                title={t('cardsWorkspace.pick.other')}
                count={otherCommon.length}
                selected={selectedIn(otherCommon)}
                open={isOpen('o')}
                onToggle={() => toggleSec('o')}
                preview={otherCommon.slice(0, 5).map((x) => x.main).join(', ') + (otherCommon.length > 5 ? '…' : '')}
              />
              {isOpen('o') && <div className="flex flex-col px-4 pb-4">{senseGroups(otherCommon)}</div>}
            </div>
          )}

          {constructions.map(([k, title, list]) => constructionSection(k, title, list))}

          {/* Редкие — в самом конце и только для чтения (решение владельца 2026-10-07). */}
          {otherRare.length + view.rarePhrases.length > 0 && (
            <div className="border-t border-[#26262b]">
              <SectionHead
                title={t('cardsWorkspace.pick.rareSection')}
                count={otherRare.length + view.rarePhrases.length}
                selected={0}
                open={isOpen('r')}
                onToggle={() => toggleSec('r')}
              />
              {isOpen('r') && (
                <div className="flex flex-col px-4 pb-4">
                  <p className="px-0.5 pb-1 text-[14px] leading-[1.4] text-[#8a8a90]">
                    {t('cardsWorkspace.pick.rareReadOnly')}
                  </p>
                  {senseGroups(otherRare, { ...ctx, readOnly: true })}
                  {view.rarePhrases.length > 0 && (
                    <div className={cn('pb-1 pt-2.5', otherRare.length > 0 && 'border-t border-[#26262b]')}>
                      <div className="flex items-baseline gap-2 px-0.5 pb-1.5">
                        <span className="text-[12px] font-semibold uppercase tracking-[0.12em] text-muted">
                          {t('cardsWorkspace.pick.rarePhrases')}
                        </span>
                        <span className="text-[12px] text-[#6a6a70]">{view.rarePhrases.length}</span>
                      </div>
                      {view.rarePhrases.map((c) => (
                        <RarePhraseRow key={c.id} c={c} t={t} />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          <div className="h-3 border-t border-[#26262b]" />
        </div>
      </div>

      <div
        className="shrink-0 border-t border-[#26262b] bg-[#17171a] px-4 pt-2.5"
        style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 8px)' }}
      >
        <div className="mx-auto flex w-full max-w-[640px] flex-col gap-2">
          {moduleSlot}
          {/* До 480 px строка остатка или пополнения — своей строкой под «1 из 10», во всю ширину и слева, значок —
              по первой строке: справа она рвалась на «останется 24 жетона из / 30» и «Новые / жетоны — в / 00:00 МСК.»
              (приёмка A1-04). Шире — справа в одну строку. */}
          <div className="flex min-h-[30px] flex-wrap items-center justify-between gap-x-3 gap-y-1">
            <span className="flex shrink-0 items-center gap-2">
              <span className="flex gap-[3px]" aria-hidden>
                {Array.from({ length: choice.limit }, (_, i) => (
                  <span
                    key={i}
                    className="h-1 w-2 rounded-sm transition-colors duration-150"
                    style={{ background: i < choice.count ? (choice.atLimit ? '#ff9a5c' : '#f36a1c') : '#34343a' }}
                  />
                ))}
              </span>
              <span className={cn('text-[14px] font-medium', choice.atLimit ? 'text-[#ff9a5c]' : 'text-muted')}>
                {choice.atLimit
                  ? t('cardsWorkspace.pick.countMaxShort', { limit: choice.limit })
                  : t('cardsWorkspace.pick.count', { n: choice.count, limit: choice.limit })}
              </span>
            </span>
            <ChoiceTokenLine
              line={price.line}
              className="min-w-0 justify-end text-right max-[479.98px]:basis-full max-[479.98px]:justify-start max-[479.98px]:text-left"
            />
          </div>
          {n > 0 && (
            <div className="line-clamp-2 text-[14px] leading-[1.4] text-[#c9c5c0]">
              {choice.cards.map((c, i) => (
                <span key={c.front}>
                  <b lang="en" className="font-serif text-[14px] font-semibold text-fg">
                    {c.front}
                  </b>
                  : {c.translations.join(', ')}
                  {i < n - 1 ? ' · ' : ''}
                </span>
              ))}
            </div>
          )}
          {price.blocked && exhaustedActions}
          {error ? (
            <p role="alert" className="text-[14px] leading-[1.4] text-danger">
              {error}
            </p>
          ) : (
            notice &&
            (noticeAction ? (
              // Причина слева, «Повторить» справа — как в превью (CompactLookup). Главная кнопка при этом
              // неактивна со своей подписью: «Повторить» на ней под пальцем стала бы платной «Создать».
              <div className="flex items-center justify-between gap-3">
                <p
                  role={notice.tone === 'danger' ? 'alert' : 'status'}
                  className={cn('min-w-0 text-[14px] leading-[1.4]', notice.tone === 'danger' ? 'text-danger' : 'text-muted')}
                >
                  {notice.text}
                </p>
                <button
                  type="button"
                  onClick={noticeAction.onClick}
                  className="flex min-h-11 shrink-0 items-center rounded-pill px-3 text-[14px] font-medium text-accent-hi active:bg-elev"
                >
                  {noticeAction.label}
                </button>
              </div>
            ) : (
              <p
                role={notice.tone === 'danger' ? 'alert' : 'status'}
                className={cn('text-[14px] leading-[1.4]', notice.tone === 'danger' ? 'text-danger' : 'text-muted')}
              >
                {notice.text}
              </p>
            ))
          )}
          <button
            type="button"
            disabled={!canCreate}
            onClick={() => canCreate && onCreate(choice.cards)}
            aria-busy={submitting || undefined}
            className={cn(
              'flex h-[54px] w-full items-center justify-center gap-2.5 rounded-pill text-[16px] font-semibold transition-colors',
              canCreate
                ? 'bg-accent text-[#141416] shadow-[0_12px_28px_-12px_rgba(243,106,28,0.7)] active:scale-[0.99]'
                : submitting
                  ? 'cursor-wait bg-accent text-[#141416] shadow-[0_12px_28px_-12px_rgba(243,106,28,0.7)]'
                  : 'bg-line text-[#8a8a90]',
            )}
          >
            {/* Пока создаётся — спиннер и «Создаём…», а не серая кнопка без объяснений. */}
            {submitting ? (
              <>
              <svg className="h-[18px] w-[18px] animate-spin motion-reduce:animate-none" viewBox="0 0 18 18" fill="none" aria-hidden>
                <circle cx="9" cy="9" r="7" stroke="currentColor" strokeOpacity="0.22" strokeWidth="2.2" />
                <path d="M16 9a7 7 0 0 0-7-7" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
              </svg>
                {t('cardsWorkspace.manual.submitting')}
              </>
            ) : (
              createLabel
            )}
          </button>
        </div>
      </div>
      {peekLayer}
    </motion.div>
  );
}
